import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import './PublicOnboardingPages.css';

const normalizeDocKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
const hasUploadedDocument = (docName, uploads = {}) => {
  const target = normalizeDocKey(docName);
  return Object.entries(uploads).some(([key, value]) => {
    if (normalizeDocKey(key) === target) return true;
    if (value && typeof value === 'object') {
      return normalizeDocKey(value.name) === target || normalizeDocKey(value.requiredName) === target;
    }
    return false;
  });
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD
  || process.env.REACT_APP_BACKEND_URL
  || 'http://localhost:8000';

const PublicDocumentUpload = () => {
  useUiLanguage();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [taskContext, setTaskContext] = useState(null);
  const [taskData, setTaskData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [done, setDone] = useState(false);

  const resolveUploadToken = useCallback(async () => {
    if (!token) throw new Error('Missing access token');
    const response = await fetch(`${BACKEND_URL}/onboarding/public/resolve-document-upload-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || 'Invalid or expired upload link');
    }
    return payload;
  }, [token]);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const payload = await resolveUploadToken();
        setTaskContext({
          companyUID: payload.companyUID,
          hireId: payload.hireId,
          stage: payload.stage,
          taskId: payload.taskId
        });
        setTaskData({
          id: payload.taskId,
          ...(payload.task || {})
        });
        setCompanyInfo(payload.company || null);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Unable to load upload request');
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [resolveUploadToken]);

  const requiredDocuments = useMemo(() => {
    const fromTask = Array.isArray(taskData?.requiredDocuments) ? taskData.requiredDocuments : [];
    const fromTypes = Array.isArray(taskData?.documentTypes) ? taskData.documentTypes : [];
    const combined = [...fromTask, ...fromTypes].map(v => String(v || '').trim()).filter(Boolean);
    return combined.length > 0 ? [...new Set(combined)] : ['Requested Document'];
  }, [taskData]);

  const uploadedDocuments = useMemo(() => {
    const data = taskData?.uploadedDocuments;
    return data && typeof data === 'object' ? data : {};
  }, [taskData]);

  useEffect(() => {
    if (!taskData) return;
    const allUploaded = requiredDocuments.every(docName => hasUploadedDocument(docName, uploadedDocuments));
    setDone((taskData.status || '').toLowerCase() === 'completed' || allUploaded);
  }, [taskData, requiredDocuments, uploadedDocuments]);

  const isDocUploaded = (docName, uploads = uploadedDocuments) => {
    return hasUploadedDocument(docName, uploads);
  };

  const handleFileSelect = (docName, file) => {
    if (!file) return;
    setSelectedFiles(prev => ({ ...prev, [docName]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskContext || !taskData) return;

    setSubmitting(true);
    setError('');
    try {
      const uploads = { ...uploadedDocuments };

      for (const docName of requiredDocuments) {
        if (isDocUploaded(docName, uploads)) continue;
        const file = selectedFiles[docName];
        if (!file) {
          throw new Error(`Please upload: ${docName}`);
        }

        const safeDoc = docName.replace(/[^a-zA-Z0-9-_ ]+/g, '').replace(/\s+/g, '_');
        const uploadedAt = Date.now();
        const storagePath = `onboarding_required_docs/${taskContext.companyUID}/${taskContext.hireId}/${taskContext.taskId}/${safeDoc}/${uploadedAt}_${file.name}`;
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);

        const registerResponse = await fetch(`${BACKEND_URL}/onboarding/public/register-document-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            documentName: docName,
            fileName: file.name,
            fileUrl,
            uploadedAt
          })
        });
        const registerPayload = await registerResponse.json().catch(() => ({}));
        if (!registerResponse.ok || !registerPayload?.success) {
          throw new Error(registerPayload?.error || `Failed to register upload for ${docName}`);
        }

        uploads[docName] = {
          name: docName,
          requiredName: docName,
          fileName: file.name,
          fileUrl,
          uploadedAt
        };
      }

      const latest = await resolveUploadToken();
      const latestTask = {
        id: latest.taskId,
        ...(latest.task || {}),
        uploadedDocuments: latest.task?.uploadedDocuments || uploads
      };
      setTaskData(latestTask);
      const allUploaded = requiredDocuments.every(docName => isDocUploaded(docName, latestTask.uploadedDocuments || uploads));
      setDone(allUploaded || latestTask.status === 'completed');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-page">
        <div className="public-page-shell">
          <div className="public-card public-status-card">
            <div className="public-status-icon loading">
              <i className="fa-solid fa-spinner fa-spin" />
            </div>
            <h2 className="public-title">{uiText("Loading Upload Request")}</h2>
            <p className="public-subtitle">{uiText("Please wait while we verify your secure link.")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-page">
        <div className="public-page-shell">
          <div className="public-card public-status-card">
            <div className="public-status-icon error">
              <i className="fa-solid fa-circle-exclamation" />
            </div>
            <h2 className="public-title">{uiText("Link Error")}</h2>
            <p className="public-subtitle">{uiText(error)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="public-page">
        <div className="public-page-shell">
          <div className="public-card public-status-card">
            <div className="public-status-icon success">
              <i className="fa-solid fa-circle-check" />
            </div>
            <h2 className="public-title">{uiText("Documents Submitted")}</h2>
            <p className="public-subtitle">{uiText("All required documents were uploaded successfully.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="public-page-shell">
        <div className="public-card">
          <div className="form-header">
            {companyInfo?.companyName && (
              <div className="public-company">
                <i className="fa-solid fa-building" />
                {companyInfo.companyName}
              </div>
            )}
            <h1 className="public-title">{taskData?.title || uiText("Upload Required Documents")}</h1>
            <p className="public-subtitle">{taskData?.description || uiText("Please upload each required document below.")}</p>
          </div>

          <form className="public-form" onSubmit={handleSubmit}>
            {requiredDocuments.map((docName) => {
              const uploaded = isDocUploaded(docName);
              return (
                <div key={docName} className={`public-doc-row ${uploaded ? 'uploaded' : ''}`}>
                  <div className="public-doc-head">
                    <div className="public-doc-name">{docName}</div>
                    <span className="public-doc-badge">{uploaded ? uiText("Uploaded") : uiText("Pending")}</span>
                  </div>
                  <input
                    className="public-file-input"
                    type="file"
                    onChange={(event) => handleFileSelect(docName, event.target.files?.[0])}
                    required={!uploaded}
                  />
                </div>
              );
            })}

            {error && <p className="public-error">{uiText(error)}</p>}

            <button type="submit" className="public-submit" disabled={submitting}>
              {submitting ? uiText("Uploading...") : uiText("Submit Documents")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicDocumentUpload;
